/* eslint-disable react/prop-types */
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  AccountTree as AccountTreeIcon,
  Add as AddIcon,
  Edit as EditIcon,
  LinkOff as LinkOffIcon,
  Security as SecurityIcon,
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

function HazardsGuideDialog({ open, onClose }) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 3,
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.15)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
          borderBottom: `2px solid ${alpha(theme.palette.warning.main, 0.3)}`,
          pb: 2,
          pt: 3,
          position: "relative",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, pr: 6 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.warning.main, 0.2),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `2px solid ${alpha(theme.palette.warning.main, 0.4)}`,
            }}
          >
            <WarningIcon sx={{ fontSize: 32, color: "warning.main" }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight="bold" color="text.primary">
              {t("hazardsGuide.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("hazardsGuide.subtitle")}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            bgcolor: alpha(theme.palette.background.paper, 0.8),
            "&:hover": {
              bgcolor: alpha(theme.palette.error.main, 0.1),
            },
          }}
          aria-label={t("hazardsGuide.closeAria")}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Overview Section */}
        <Box
          sx={{
            p: 3,
            bgcolor: alpha(theme.palette.info.main, 0.05),
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <InfoIcon sx={{ color: "info.main", fontSize: 24 }} />
            <Typography variant="h6" fontWeight="bold" color="info.main">
              {t("hazardsGuide.overviewTitle")}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" paragraph>
            {t("hazardsGuide.overviewText")}
          </Typography>
        </Box>

        {/* Main Content */}
        <Box sx={{ p: 3 }}>
          {/* Section 1: Understanding the Layout */}
          <Accordion
            defaultExpanded
            sx={{
              mb: 2,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              borderRadius: 2,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                borderRadius: "8px 8px 0 0",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Chip
                  label="1"
                  size="small"
                  sx={{
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    fontWeight: "bold",
                  }}
                />
                <Typography variant="subtitle1" fontWeight="bold">
                  {t("hazardsGuide.section1Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" paragraph>
                {t("hazardsGuide.section1Text")}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <AccountTreeIcon sx={{ color: "primary.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("hazardsGuide.section1Point1")}
                    secondary={t("hazardsGuide.section1Point1Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <WarningIcon sx={{ color: "warning.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("hazardsGuide.section1Point2")}
                    secondary={t("hazardsGuide.section1Point2Desc")}
                  />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          {/* Section 2: Linking Hazards to Work Types */}
          <Accordion
            sx={{
              mb: 2,
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
              borderRadius: 2,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.success.main, 0.05),
                borderRadius: "8px 8px 0 0",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Chip
                  label="2"
                  size="small"
                  sx={{
                    bgcolor: "success.main",
                    color: "success.contrastText",
                    fontWeight: "bold",
                  }}
                />
                <Typography variant="subtitle1" fontWeight="bold">
                  {t("hazardsGuide.section2Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" paragraph>
                {t("hazardsGuide.section2Text")}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("hazardsGuide.section2Step1")}
                    secondary={t("hazardsGuide.section2Step1Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("hazardsGuide.section2Step2")}
                    secondary={t("hazardsGuide.section2Step2Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("hazardsGuide.section2Step3")}
                    secondary={t("hazardsGuide.section2Step3Desc")}
                  />
                </ListItem>
              </List>
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: alpha(theme.palette.info.main, 0.05),
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  color="info.main"
                  display="block"
                  gutterBottom
                >
                  💡 {t("hazardsGuide.proTip")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("hazardsGuide.section2ProTip")}
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Section 3: Removing Hazard Links */}
          <Accordion
            sx={{
              mb: 2,
              border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
              borderRadius: 2,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.warning.main, 0.05),
                borderRadius: "8px 8px 0 0",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Chip
                  label="3"
                  size="small"
                  sx={{
                    bgcolor: "warning.main",
                    color: "warning.contrastText",
                    fontWeight: "bold",
                  }}
                />
                <Typography variant="subtitle1" fontWeight="bold">
                  {t("hazardsGuide.section3Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" paragraph>
                {t("hazardsGuide.section3Text")}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <LinkOffIcon sx={{ color: "warning.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("hazardsGuide.section3Step1")}
                    secondary={t("hazardsGuide.section3Step1Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <LinkOffIcon sx={{ color: "warning.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("hazardsGuide.section3Step2")}
                    secondary={t("hazardsGuide.section3Step2Desc")}
                  />
                </ListItem>
              </List>
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: alpha(theme.palette.warning.main, 0.05),
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  color="warning.main"
                  display="block"
                  gutterBottom
                >
                  ⚠️ {t("hazardsGuide.important")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("hazardsGuide.section3Important")}
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Section 4: Adding Precautions to Hazards */}
          <Accordion
            sx={{
              mb: 2,
              border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
              borderRadius: 2,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.secondary.main, 0.05),
                borderRadius: "8px 8px 0 0",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Chip
                  label="4"
                  size="small"
                  sx={{
                    bgcolor: "secondary.main",
                    color: "secondary.contrastText",
                    fontWeight: "bold",
                  }}
                />
                <Typography variant="subtitle1" fontWeight="bold">
                  {t("hazardsGuide.section4Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" paragraph>
                {t("hazardsGuide.section4Text")}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <AddIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("hazardsGuide.section4Step1")}
                    secondary={t("hazardsGuide.section4Step1Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <SecurityIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("hazardsGuide.section4Step2")}
                    secondary={t("hazardsGuide.section4Step2Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("hazardsGuide.section4Step3")}
                    secondary={t("hazardsGuide.section4Step3Desc")}
                  />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          {/* Section 5: Adjusting Risk Levels */}
          <Accordion
            sx={{
              mb: 2,
              border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
              borderRadius: 2,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.error.main, 0.05),
                borderRadius: "8px 8px 0 0",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Chip
                  label="5"
                  size="small"
                  sx={{
                    bgcolor: "error.main",
                    color: "error.contrastText",
                    fontWeight: "bold",
                  }}
                />
                <Typography variant="subtitle1" fontWeight="bold">
                  {t("hazardsGuide.section5Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" paragraph>
                {t("hazardsGuide.section5Text")}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <EditIcon sx={{ color: "primary.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("hazardsGuide.section5Step1")}
                    secondary={t("hazardsGuide.section5Step1Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <AssessmentIcon sx={{ color: "primary.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("hazardsGuide.section5Step2")}
                    secondary={t("hazardsGuide.section5Step2Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("hazardsGuide.section5Step3")}
                    secondary={t("hazardsGuide.section5Step3Desc")}
                  />
                </ListItem>
              </List>
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: alpha(theme.palette.info.main, 0.05),
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  color="info.main"
                  display="block"
                  gutterBottom
                >
                  💡 {t("hazardsGuide.proTip")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("hazardsGuide.section5ProTip")}
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Divider sx={{ my: 3 }} />

          {/* Quick Reference */}
          <Box
            sx={{
              p: 3,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              color="primary.main"
              gutterBottom
            >
              📋 {t("hazardsGuide.quickRefTitle")}
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary={
                    <Typography variant="body2">
                      <strong>{t("hazardsGuide.quickRef1Title")}:</strong>{" "}
                      {t("hazardsGuide.quickRef1Text")}
                    </Typography>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={
                    <Typography variant="body2">
                      <strong>{t("hazardsGuide.quickRef2Title")}:</strong>{" "}
                      {t("hazardsGuide.quickRef2Text")}
                    </Typography>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={
                    <Typography variant="body2">
                      <strong>{t("hazardsGuide.quickRef3Title")}:</strong>{" "}
                      {t("hazardsGuide.quickRef3Text")}
                    </Typography>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={
                    <Typography variant="body2">
                      <strong>{t("hazardsGuide.quickRef4Title")}:</strong>{" "}
                      {t("hazardsGuide.quickRef4Text")}
                    </Typography>
                  }
                />
              </ListItem>
            </List>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.background.default, 0.5),
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          size="large"
          sx={{
            borderRadius: 2,
            px: 4,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {t("hazardsGuide.closeButton")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default HazardsGuideDialog;
